import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  SlidersHorizontal
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PipelineActivityReport } from '../components/PipelineActivityReport';
import { PipelineLineReport } from '../components/PipelineLineReport';
import { ReportDateRangePicker } from '../date-range/ReportDateRangePicker';
import {
  PRESETS,
  businessTodayAsLocalDate,
  makeCustom,
  makePreset,
  type PresetId,
  type ReportPeriod
} from '../date-range/reportPeriod';
import type { DatePreset, PipelineLoan, PipelineStatus } from '../types';
import { PerformanceCohorts } from './PerformanceCohorts';
import { PerformanceLoanSheet } from './PerformanceLoanSheet';
import { PerformanceOverview } from './PerformanceOverview';
import { exportPipelinePerformanceWorkbook } from './pipelinePerformanceExport';
import type {
  CohortBasis,
  PerformanceLoanDetail,
  PipelinePerformanceFilterOptions,
  PipelinePerformanceFilters,
  PipelinePerformanceViewModel
} from './types';
import { formatDateRange } from './formatters';

export interface PipelinePerformanceViewProps {
  loans: PipelineLoan[];
  model: PipelinePerformanceViewModel | null;
  period: ReportPeriod;
  filters: PipelinePerformanceFilters;
  filterOptions: PipelinePerformanceFilterOptions;
  isLoading: boolean;
  onPeriodChange: (period: ReportPeriod) => void;
  onFiltersChange: (filters: PipelinePerformanceFilters) => void;
  error?: string | null;
  warnings?: string[];
  refreshedAt?: Date | null;
  onRefresh?: () => void;
  onExport?: () => void | Promise<void>;
}

type PerformanceTab = 'overview' | 'cohorts' | 'milestone-flow' | 'team-activity';

interface LoanSheetState {
  title: string;
  description: string;
  loans: PerformanceLoanDetail[];
}

interface FilterControlsProps {
  period: ReportPeriod;
  filters: PipelinePerformanceFilters;
  options: PipelinePerformanceFilterOptions;
  onPeriodChange: PipelinePerformanceViewProps['onPeriodChange'];
  onFiltersChange: PipelinePerformanceViewProps['onFiltersChange'];
}

function FilterSelect({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full md:w-44" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label.toLowerCase()}s</SelectItem>
        {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function FilterControls({
  period,
  filters,
  options,
  onPeriodChange,
  onFiltersChange
}: FilterControlsProps) {
  const updateFilter = (key: keyof PipelinePerformanceFilters, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };
  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      <div className="max-w-full pb-1 md:pb-0">
        <ReportDateRangePicker
          value={period}
          onChange={onPeriodChange}
          max={businessTodayAsLocalDate()}
        />
      </div>
      <FilterSelect label="Loan officer" value={filters.loanOfficer} options={options.loanOfficers} onChange={(value) => updateFilter('loanOfficer', value)} />
      <FilterSelect label="Investor" value={filters.investor} options={options.investors} onChange={(value) => updateFilter('investor', value)} />
      <FilterSelect label="Channel" value={filters.channel} options={options.channels} onChange={(value) => updateFilter('channel', value)} />
      <FilterSelect label="Product" value={filters.product} options={options.products} onChange={(value) => updateFilter('product', value)} />
    </div>
  );
}

function localDateFromKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function MobileFilterControls({
  period,
  filters,
  options,
  onPeriodChange,
  onFiltersChange
}: FilterControlsProps) {
  const max = businessTodayAsLocalDate();
  const maxKey = format(max, 'yyyy-MM-dd');
  const updateFilter = (key: keyof PipelinePerformanceFilters, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="performance-mobile-period" className="text-sm font-medium">Period</label>
        <Select
          value={period.mode}
          onValueChange={(value) => {
            onPeriodChange(value === 'custom'
              ? makeCustom(period.from, period.to)
              : makePreset(value as PresetId, max));
          }}
        >
          <SelectTrigger id="performance-mobile-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESETS.map((preset) => (
              <SelectItem key={preset.id} value={preset.id}>{preset.label}</SelectItem>
            ))}
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1 text-sm">
          <span className="font-medium">From</span>
          <Input
            type="date"
            value={format(period.from, 'yyyy-MM-dd')}
            max={maxKey}
            onChange={(event) => {
              if (event.target.value) {
                onPeriodChange(makeCustom(localDateFromKey(event.target.value), period.to));
              }
            }}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-medium">To</span>
          <Input
            type="date"
            value={format(period.to, 'yyyy-MM-dd')}
            max={maxKey}
            onChange={(event) => {
              if (event.target.value) {
                onPeriodChange(makeCustom(period.from, localDateFromKey(event.target.value)));
              }
            }}
          />
        </label>
      </div>
      <div className="space-y-2">
        <FilterSelect label="Loan officer" value={filters.loanOfficer} options={options.loanOfficers} onChange={(value) => updateFilter('loanOfficer', value)} />
        <FilterSelect label="Investor" value={filters.investor} options={options.investors} onChange={(value) => updateFilter('investor', value)} />
        <FilterSelect label="Channel" value={filters.channel} options={options.channels} onChange={(value) => updateFilter('channel', value)} />
        <FilterSelect label="Product" value={filters.product} options={options.products} onChange={(value) => updateFilter('product', value)} />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4" aria-label="Loading pipeline performance">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-44 rounded-xl" />)}
      </div>
      <Skeleton className="h-80 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

function periodToLegacyPreset(period: ReportPeriod): DatePreset {
  if (period.mode === 'mtd') {
    return 'mtd';
  }
  if (period.mode === 'ytd') {
    return 'ytd';
  }
  return 'custom';
}

export function PipelinePerformanceView({
  loans,
  model,
  period,
  filters,
  filterOptions,
  isLoading,
  onPeriodChange,
  onFiltersChange,
  error = null,
  warnings = [],
  refreshedAt = null,
  onRefresh,
  onExport
}: PipelinePerformanceViewProps) {
  const [activeTab, setActiveTab] = useState<PerformanceTab>('overview');
  const [cohortBasis, setCohortBasis] = useState<CohortBasis>('application');
  const [loanSheet, setLoanSheet] = useState<LoanSheetState | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [lineSelectedStatus, setLineSelectedStatus] = useState<PipelineStatus | 'all'>('all');
  const [activitySeed, setActivitySeed] = useState<{ status: PipelineStatus } | null>(null);

  const allWarnings = useMemo(() => Array.from(new Set([
    ...warnings,
    ...(model?.warnings || [])
  ].filter(Boolean))), [model?.warnings, warnings]);
  const activeFilterCount = Object.values(filters).filter((value) => value !== 'all').length;

  const openLoans = (title: string, description: string, detailLoans: PerformanceLoanDetail[]) => {
    setLoanSheet({ title, description, loans: detailLoans });
  };

  const handleExport = async () => {
    if (!model || isExporting) {
      return;
    }
    setExportError(null);
    setIsExporting(true);
    try {
      if (onExport) {
        await onExport();
      } else {
        await exportPipelinePerformanceWorkbook({ model, loans, filters, refreshedAt });
      }
    } catch {
      setExportError('The workbook could not be generated. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const drillToActivity = (seed: { status: PipelineStatus }) => {
    setActivitySeed({ status: seed.status });
    setActiveTab('team-activity');
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Pipeline Performance</h1>
            {model?.currentRange.includesToday && <Badge variant="secondary">Includes today</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Reconciled Created, Application, Locked, and Funded events across the selected production window.
          </p>
          {model && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{formatDateRange(model.currentRange.from, model.currentRange.to)} · {model.currentRange.businessDays} business days</span>
              <span>
                Compare: {model.comparisonRange.isEmpty
                  ? model.comparisonRange.label
                  : `${formatDateRange(model.comparisonRange.from, model.comparisonRange.to)} · ${model.comparisonRange.businessDays} business days`}
              </span>
              <span>Refreshed {refreshedAt ? format(refreshedAt, 'MMM d, yyyy h:mm a') : '—'}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!model || isLoading || isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting…' : 'Export workbook'}
          </Button>
        </div>
      </header>

      <div className="hidden rounded-lg border bg-card p-3 md:block">
        <FilterControls
          period={period}
          filters={filters}
          options={filterOptions}
          onPeriodChange={onPeriodChange}
          onFiltersChange={onFiltersChange}
        />
      </div>

      <div className="md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="inline-flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters and date range
              </span>
              {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex h-full w-full max-w-none flex-col sm:max-w-md">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Report filters</SheetTitle>
              <SheetDescription>Filters apply to every performance tab and workbook sheet.</SheetDescription>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-auto py-4">
              <MobileFilterControls
                period={period}
                filters={filters}
                options={filterOptions}
                onPeriodChange={onPeriodChange}
                onFiltersChange={onFiltersChange}
              />
            </div>
            <SheetFooter>
              <SheetClose asChild><Button className="w-full">View report</Button></SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Pipeline performance could not load</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>{error}</span>
            {onRefresh && <Button variant="outline" size="sm" onClick={onRefresh}>Try again</Button>}
          </AlertDescription>
        </Alert>
      )}

      {allWarnings.length > 0 && !error && (
        <Alert className="border-warning/40 bg-warning/5">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertTitle>Partial data warning</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {allWarnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {exportError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Export failed</AlertTitle>
          <AlertDescription>{exportError}</AlertDescription>
        </Alert>
      )}

      {isLoading && !model ? (
        <LoadingState />
      ) : !error && model ? (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as PerformanceTab)}>
          <div className="overflow-x-auto pb-1">
            <TabsList className="w-max" aria-label="Pipeline Performance reports">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cohorts">Cohorts</TabsTrigger>
              <TabsTrigger value="milestone-flow">Milestone Flow</TabsTrigger>
              <TabsTrigger value="team-activity">Team Activity</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="overview" className="mt-4">
            <PerformanceOverview model={model} onOpenLoans={openLoans} />
          </TabsContent>
          <TabsContent value="cohorts" className="mt-4">
            <PerformanceCohorts
              model={model}
              basis={cohortBasis}
              onBasisChange={setCohortBasis}
              onOpenLoans={openLoans}
            />
          </TabsContent>
          <TabsContent value="milestone-flow" className="mt-4">
            <PipelineLineReport
              loans={loans}
              isLoading={isLoading}
              dateRange={{ from: period.from, to: period.to }}
              datePreset={periodToLegacyPreset(period)}
              selectedStatus={lineSelectedStatus}
              onStatusChange={setLineSelectedStatus}
              onDrillToActivity={drillToActivity}
            />
          </TabsContent>
          <TabsContent value="team-activity" className="mt-4">
            <PipelineActivityReport
              loans={loans}
              isLoading={isLoading}
              dateRange={{ from: period.from, to: period.to }}
              datePreset={periodToLegacyPreset(period)}
              seed={activitySeed}
              onSeedConsumed={() => setActivitySeed(null)}
            />
          </TabsContent>
        </Tabs>
      ) : !error ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-sm text-muted-foreground">
          No performance data is available for this range.
        </div>
      ) : null}

      <PerformanceLoanSheet
        open={Boolean(loanSheet)}
        title={loanSheet?.title || 'Contributing loans'}
        description={loanSheet?.description || ''}
        loans={loanSheet?.loans || []}
        onOpenChange={(open) => {
          if (!open) {
            setLoanSheet(null);
          }
        }}
      />
    </div>
  );
}
